import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
} from "../../../components/ui/dialog";
import { X, BookOpen, AlertTriangle, Info } from "lucide-react";
import { PromptExamplesModal } from "./PromptExamplesModal";

export const ClaudeSettingsDialog = ({
  isOpen,
  onClose,
  anthropicKey,
  anthropicModel,
  anthropicPrompt,
  temperature,
  audioProvider,
  audioApiKey,
  existingOpenaiKey,
  existingGeminiKey,
  onSave,
}) => {
  const ensureMinimumTemperature = (value) => Math.max(0.01, Number.parseFloat(value) || 0.01);

  const [localKey, setLocalKey] = useState(anthropicKey || "");
  const [localModel, setLocalModel] = useState(anthropicModel || "claude-sonnet-4-6");
  const [localPrompt, setLocalPrompt] = useState(anthropicPrompt || "");
  const [localTemperature, setLocalTemperature] = useState(ensureMinimumTemperature(temperature || 0.7));
  const [localAudioProvider, setLocalAudioProvider] = useState(audioProvider || "OPENAI");
  const [localAudioApiKey, setLocalAudioApiKey] = useState(audioApiKey || "");
  const [isExamplesModalOpen, setIsExamplesModalOpen] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const handleSave = () => {
    onSave({
      anthropicKey: localKey,
      anthropicModel: localModel,
      anthropicPrompt: localPrompt,
      temperature: ensureMinimumTemperature(localTemperature),
      audioProvider: localAudioProvider,
      audioApiKey: localAudioApiKey,
    });
  };

  useEffect(() => {
    if (isOpen) {
      setLocalKey(anthropicKey || "");
      setLocalModel(anthropicModel || "claude-sonnet-4-6");
      setLocalPrompt(anthropicPrompt || "");
      setLocalTemperature(ensureMinimumTemperature(temperature || 0.7));
      setLocalAudioProvider(audioProvider || "OPENAI");
      setLocalAudioApiKey(audioApiKey || "");
      setAutoFilled(false);
    }
  }, [isOpen, anthropicKey, anthropicModel, anthropicPrompt, temperature, audioProvider, audioApiKey]);

  useEffect(() => {
    if (!isOpen) return;

    const currentAudioKey = localAudioApiKey;
    if (currentAudioKey && currentAudioKey.trim().length > 0) return;

    const matchingKey = localAudioProvider === "OPENAI" ? existingOpenaiKey : existingGeminiKey;
    if (matchingKey && matchingKey.trim().length > 0) {
      setLocalAudioApiKey(matchingKey);
      setAutoFilled(true);
    } else {
      setAutoFilled(false);
    }
  }, [isOpen, localAudioProvider, existingOpenaiKey, existingGeminiKey]);

  const hasAudioKey = localAudioApiKey && localAudioApiKey.trim().length > 0;
  const audioProviderLabel = localAudioProvider === "OPENAI" ? "OpenAI" : "Gemini";

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogPortal>
          <DialogOverlay className="z-[8999] bg-black/80 backdrop-blur-md" />
          <DialogContent
            className="max-w-[850px] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-black/50 relative overflow-hidden"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9001
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
            <div className="absolute top-16 left-24 w-40 h-40 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
            <div className="absolute bottom-24 right-20 w-48 h-48 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>
            <div className="absolute top-40 right-40 w-28 h-28 bg-effectIndigo rounded-full blur-2xl animate-pulse pointer-events-none" style={{ animationDuration: '6s', animationDelay: '4s' }}></div>
            <div className="absolute bottom-16 left-16 w-36 h-36 bg-effectCyan rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '7s', animationDelay: '3s' }}></div>

            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
            >
              <X className="w-4 h-4" />
            </button>

            <DialogHeader className="relative z-10">
              <DialogTitle className="flex items-center gap-4 text-white">
                <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800/80 via-orange-500/30 to-amber-500/40 shadow-lg border border-white/20">
                  <span className="text-xl drop-shadow-sm">🧠</span>
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-500/8 rounded-xl animate-pulse-subtle"></div>
                </div>
                <span className="text-xl font-semibold">Configurações do Claude</span>
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-6 relative z-10 overflow-y-auto max-h-[calc(100vh-220px)] pr-2">
              <div>
                <label htmlFor="anthropic-key" className="block text-sm font-medium text-textOnDarkStrong mb-2">
                  Anthropic API Key
                </label>
                <input
                  id="anthropic-key"
                  type="password"
                  value={localKey}
                  onChange={(e) => setLocalKey(e.target.value)}
                  className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
                  placeholder="sk-ant-..."
                />
              </div>

              <div>
                <label htmlFor="anthropic-model" className="block text-sm font-medium text-textOnDarkStrong mb-2">
                  Modelo do Claude
                </label>
                <select
                  id="anthropic-model"
                  value={localModel}
                  onChange={(e) => setLocalModel(e.target.value)}
                  className="w-full rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <optgroup label="Claude 4.8" className="bg-slate-800 text-white">
                    <option value="claude-opus-4-8" className="bg-slate-800 text-white">Claude Opus 4.8</option>
                  </optgroup>
                  <optgroup label="Claude 4.7" className="bg-slate-800 text-white">
                    <option value="claude-opus-4-7" className="bg-slate-800 text-white">Claude Opus 4.7</option>
                  </optgroup>
                  <optgroup label="Claude 4.6" className="bg-slate-800 text-white">
                    <option value="claude-opus-4-6" className="bg-slate-800 text-white">Claude Opus 4.6</option>
                    <option value="claude-sonnet-4-6" className="bg-slate-800 text-white">Claude Sonnet 4.6</option>
                  </optgroup>
                  <optgroup label="Claude 4.5" className="bg-slate-800 text-white">
                    <option value="claude-haiku-4-5-20251001" className="bg-slate-800 text-white">Claude Haiku 4.5</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="anthropic-prompt" className="block text-sm font-medium text-textOnDarkStrong">
                    Prompt
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsExamplesModalOpen(true)}
                    className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    <BookOpen className="w-3 h-3" />
                    Ver exemplos
                  </button>
                </div>
                <textarea
                  id="anthropic-prompt"
                  value={localPrompt}
                  onChange={(e) => setLocalPrompt(e.target.value)}
                  rows={6}
                  className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg resize-y placeholder-grayText"
                  placeholder="Insira o prompt do sistema..."
                />
              </div>

              <div>
                <label htmlFor="anthropic-temperature" className="block text-sm font-medium text-textOnDarkStrong mb-2">
                  Temperature: {localTemperature}
                </label>
                <input
                  id="anthropic-temperature"
                  type="range"
                  min="0.01"
                  max="1"
                  step="0.01"
                  value={localTemperature}
                  onChange={(e) => setLocalTemperature(Number.parseFloat(e.target.value))}
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-xs text-grayText mt-1">
                  <span>Preciso</span>
                  <span>Criativo</span>
                </div>
              </div>

              <div className="border-t border-modalBorder/50 pt-6">
                <h3 className="text-lg font-semibold text-textOnDarkStrong mb-4 flex items-center gap-2">
                  Transcrição de Áudio
                </h3>
                <p className="text-xs text-grayText mb-4">
                  O Claude não possui transcrição de áudio nativa. Selecione um provedor para transcrever mensagens de áudio.
                </p>

                <div className="mb-4">
                  <label htmlFor="audio-provider" className="block text-sm font-medium text-textOnDarkStrong mb-2">
                    Provedor de Áudio
                  </label>
                  <select
                    id="audio-provider"
                    value={localAudioProvider}
                    onChange={(e) => {
                      setLocalAudioProvider(e.target.value);
                      setLocalAudioApiKey("");
                      setAutoFilled(false);
                    }}
                    className="w-full rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="OPENAI" className="bg-slate-800 text-white">OpenAI (Whisper)</option>
                    <option value="GEMINI" className="bg-slate-800 text-white">Gemini</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="audio-api-key" className="block text-sm font-medium text-textOnDarkStrong mb-2">
                    Chave de API do {audioProviderLabel}
                  </label>
                  <input
                    id="audio-api-key"
                    type="password"
                    value={localAudioApiKey}
                    onChange={(e) => {
                      setLocalAudioApiKey(e.target.value);
                      setAutoFilled(false);
                    }}
                    className="w-full rounded-lg border border-emeraldBase/25 bg-modalOverlaySoft backdrop-blur-xl px-4 py-3 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-lg placeholder-grayText"
                    placeholder={localAudioProvider === "OPENAI" ? "sk-..." : "AI..."}
                  />

                  {autoFilled && hasAudioKey && (
                    <div className="mt-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-300 leading-relaxed">
                        Chave preenchida automaticamente a partir das configurações do {audioProviderLabel}
                      </p>
                    </div>
                  )}

                  {!hasAudioKey && (
                    <div className="mt-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-yellow-300 leading-relaxed">
                        Sem uma chave de API do {audioProviderLabel}, mensagens de áudio não serão respondidas
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-lg border border-modalBorder text-textOnDarkMedium hover:text-white hover:bg-modalOverlaySoft transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-emeraldBase to-emeraldMedium text-white font-medium hover:shadow-lg hover:shadow-emeraldBase/25 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  Salvar
                </button>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      <PromptExamplesModal
        isOpen={isExamplesModalOpen}
        onClose={() => setIsExamplesModalOpen(false)}
        onSelectExample={(example) => {
          setLocalPrompt(example);
          setIsExamplesModalOpen(false);
        }}
      />
    </>
  );
};
